import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    BuildingOfficeIcon,
    UserCircleIcon,
    TicketIcon,
    ShieldCheckIcon,
    DocumentTextIcon,
    ChevronLeftIcon,
    LockClosedIcon,
    PencilSquareIcon,
    CloudArrowUpIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { countries } from '../utils/countries';

const energySources = [
    { code: 'F01010101', name: 'Solar Fuel (Primary)' },
    { code: 'F01020202', name: 'Wind Fuel' },
    { code: 'F01030303', name: 'Water Fuel (Hydro)' },
    { code: 'F01040404', name: 'Geothermal Energy' },
    { code: 'F01050505', name: 'Aerothermal Energy' },
    { code: 'F04000000', name: 'Biomass (Solid/Liquid/Gas)' },
];

const technologies = [
    { code: 'T010000', name: 'Solar Photovoltaic (PV)' },
    { code: 'T010100', name: 'Solar Thermal (Concentrated)' },
    { code: 'T020000', name: 'Wind Onshore' },
    { code: 'T020001', name: 'Wind Offshore' },
    { code: 'T030100', name: 'Hydro Run-of-river' },
    { code: 'T030200', name: 'Hydro Reservoir (Dam)' },
    { code: 'T040000', name: 'Combined Heat & Power (CHP)' },
];

const supportSchemes = [
    { code: 'NONE', name: 'No Support (Market Based)' },
    { code: 'FEED_IN_TARIFF', name: 'Feed-in Tariff (FiT)' },
    { code: 'INVESTMENT_GRANT', name: 'Capital Investment Grant' },
    { code: 'PRODUCTION_GRANT', name: 'Production Based Subsidy' },
    { code: 'TAX_CREDIT', name: 'National Tax Credit' },
];

const qualityLabels = [
    { code: 'NONE', name: 'No Label' },
    { code: 'EKOENERGY', name: 'EKOenergy (Global Standard)' },
    { code: 'GREEN_E', name: 'Green-e (US Renewable)' },
    { code: 'NATUREMADE', name: 'Naturemade (Swiss Star)' },
    { code: 'TUV_SUD', name: 'TUV SÜD CMS 71 (European)' },
];

const StepItem = ({ label, active, icon: Icon, onClick, completed }) => (
    <button
        onClick={onClick}
        className={`flex flex-col items-center group transition-all ${active ? 'text-[#155e75]' : 'text-gray-300 hover:text-gray-500'}`}
    >
        <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center font-bold mb-2 transition-all ${active ? 'border-[#155e75] bg-blue-50 shadow-inner' : completed ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white group-hover:border-gray-300'}`}>
            <Icon className={`w-5 h-5 ${active ? 'text-[#155e75]' : completed ? 'text-emerald-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
    </button>
);

export default function DeviceDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [locked, setLocked] = useState(false);
    const [activeTab, setActiveTab] = useState('General');
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});

    const [profile, setProfile] = useState(null);

    useEffect(() => {
        axios.get(`/api/v1/devices`).then((deviceRes) => {
            const found = deviceRes.data.find(d => String(d.id) === String(id));
            setDevice(found);
            if (found) {
                setEditData({
                    ...found,
                    license_num: found.license_num || 'LIC-2024-GR-001',
                    license_exp: found.license_exp || '2028-12-31'
                });
            }
        }).catch(err => console.error(err)).finally(() => setLoading(false));

        axios.get(`/api/v1/accounts/profile/me`).then((profileRes) => {
            if (profileRes.data) {
                setProfile(profileRes.data);
            }
        }).catch(err => console.error('No company profile found', err));
    }, [id]);

    const handleSave = async () => {
        const email = localStorage.getItem('userEmail') || 'admin@map.go';
        const role = localStorage.getItem('userRole') || 'ADMIN';
        const headers = { 'X-User-Email': email, 'X-User-Role': role };

        try {
            const res = await axios.put(`/api/v1/devices/${id}`, editData, { headers });
            setDevice(res.data);
            setIsEditing(false);
            alert("Integrated Asset Manifest synchronized. All regulatory tiers updated.");
        } catch (e) {
            alert("Failed to save changes.");
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-40 gap-4">
            <ArrowPathIcon className="w-10 h-10 text-[#155e75] animate-spin" />
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Pulling immutable asset state...</p>
        </div>
    );
    if (!device) return <div className="p-20 text-center font-bold text-red-400 uppercase tracking-widest text-xs">Plant ID not recognized.</div>;

    return (
        <div className="space-y-6 animate-fade-in max-w-7xl mx-auto pb-20">
            <button
                onClick={() => navigate('/devices')}
                className="flex items-center gap-1 text-[10px] font-black text-[#155e75] uppercase tracking-widest hover:underline"
            >
                <ChevronLeftIcon className="w-4 h-4" /> Back to registry index
            </button>

            <div className="flex items-center justify-between">
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Technical ID: {device.id.substring(0, 8)}</p>
                        {locked && <span className="flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-[9px] font-black uppercase rounded-sm border border-red-100"><LockClosedIcon className="w-3 h-3" /> System Lock</span>}
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800 uppercase tracking-tight leading-none">{device.device_name}</h1>
                    <div className="flex items-center gap-4">
                        <p className="text-sm text-[#155e75] font-mono font-bold tracking-tight bg-blue-50 px-2 py-1 rounded">GSRN: {device.gsrn}</p>
                        <span className={`px-3 py-1 rounded-sm text-[10px] font-black uppercase tracking-widest ${locked ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {locked ? 'Administrative Lock' : 'Certified (ACTIVE)'}
                        </span>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setIsEditing(!isEditing)} className="flex items-center gap-2 px-8 py-3 bg-[#155e75] text-white text-[10px] font-black uppercase tracking-widest rounded-sm hover:bg-[#0e4e61] transition-all shadow-lg">
                        <PencilSquareIcon className="w-4 h-4" /> {isEditing ? 'Cancel Session' : 'Edit Technical File'}
                    </button>
                    {localStorage.getItem('role') === 'ADMIN' && !locked && (
                        <button onClick={() => setLocked(true)} className="p-3 border border-gray-200 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all" title="Enforce Lock">
                            <LockClosedIcon className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            <div className="glass-card rounded-none border-gray-200 bg-white shadow-xl overflow-hidden">
                <div className="step-header bg-gray-50/80 p-8 flex justify-between items-center border-b border-gray-100 px-24">
                    <StepItem label="General" icon={BuildingOfficeIcon} active={activeTab === 'General'} onClick={() => setActiveTab('General')} completed={activeTab !== 'General'} />
                    <div className="flex-1 h-[2px] bg-gray-200 mx-6" />
                    <StepItem label="Organization" icon={UserCircleIcon} active={activeTab === 'Organization'} onClick={() => setActiveTab('Organization')} completed={['Meter', 'License', 'Documents'].includes(activeTab)} />
                    <div className="flex-1 h-[2px] bg-gray-200 mx-6" />
                    <StepItem label="Meter" icon={TicketIcon} active={activeTab === 'Meter'} onClick={() => setActiveTab('Meter')} completed={['License', 'Documents'].includes(activeTab)} />
                    <div className="flex-1 h-[2px] bg-gray-200 mx-6" />
                    <StepItem label="License" icon={ShieldCheckIcon} active={activeTab === 'License'} onClick={() => setActiveTab('License')} completed={activeTab === 'Documents'} />
                    <div className="flex-1 h-[2px] bg-gray-200 mx-6" />
                    <StepItem label="Documents" icon={DocumentTextIcon} active={activeTab === 'Documents'} onClick={() => setActiveTab('Documents')} />
                </div>

                <div className="p-12 min-h-[500px]">
                    {activeTab === 'General' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 animate-fade-in">
                            <Section header="Technical Parameters">
                                <DataField label="Plant Name" value={editData.device_name} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, device_name: v })} />
                                <DataField label="Energy Source" value={editData.energy_source_code} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, energy_source_code: v })} options={energySources} />
                                <DataField label="Technology" value={editData.technology_code} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, technology_code: v })} options={technologies} />
                                <DataField label="GSRN (18-Digit)" value={editData.gsrn} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, gsrn: v })} />
                                <DataField label="Grid Connection" value={editData.grid_reference || 'National Primary Grid'} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, grid_reference: v })} />
                                <DataField label="Capacity (MW)" value={editData.capacity_mw} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, capacity_mw: v })} />
                                <DataField label="Commissioning" value={editData.commissioned_date ? editData.commissioned_date.substring(0, 10) : 'UNSET'} />
                            </Section>
                            <Section header="Location Data">
                                <DataField label="Street" value={editData.address_street} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, address_street: v })} />
                                <DataField label="City" value={editData.city} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, city: v })} />
                                <DataField label="Jurisdiction" value={editData.country_code || "National Domain"} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, country_code: v })} options={countries} />
                            </Section>
                        </div>
                    )}

                    {activeTab === 'Organization' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 animate-fade-in">
                            <Section header="Legal Entity">
                                <DataField label="Registrant Name" value={profile?.account_name || "Self-Registered Entity"} />
                                <DataField label="Company VAT" value={profile?.vat_number || "PENDING ISSUING BODY MAPPING"} />
                            </Section>
                            <Section header="Authorized Contacts">
                                <DataField label="Regulatory Officer" value="Verified Session Owner" />
                                <DataField label="Contact Email" value={localStorage.getItem('userEmail') || ''} />
                            </Section>
                        </div>
                    )}

                    {activeTab === 'Meter' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 animate-fade-in">
                            <Section header="Metering Infrastructure">
                                <DataField label="Meter Serial Number" value={editData.meter_serial || editData.meter_id} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, meter_serial: v })} />
                                <DataField label="Meter Model / Type" value={`${editData.meter_manufacturer || ''} ${editData.meter_model || ''}`} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, meter_model: v })} />
                                <DataField label="Grid Reference Node" value={editData.grid_reference || 'UNSET'} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, grid_reference: v })} />
                            </Section>
                            <Section header="Data Synchronization">
                                <DataField label="Last Sync" value="Pending Smart Meter Interconnect" />
                                <DataField label="Sync Protocol" value="IEC 62056 / SSL" />
                            </Section>
                        </div>
                    )}

                    {activeTab === 'License' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 animate-fade-in">
                            <Section header="Licensing & Schemes">
                                <DataField label="Support Scheme" value={editData.support_scheme_type} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, support_scheme_type: v })} options={supportSchemes} />
                                <DataField label="Quality Label" value={editData.label_scheme} isEditing={isEditing} onChange={(v) => setEditData({ ...editData, label_scheme: v })} options={qualityLabels} />
                            </Section>
                            <Section header="Regulatory Status">
                                <DataField label="Verification Tier" value="Standard EECS v4" />
                                <DataField label="Status" value={editData.status} />
                            </Section>
                        </div>
                    )}

                    {activeTab === 'Documents' && (
                        <div className="space-y-8 animate-fade-in">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest underline decoration-[#155e75] decoration-4 underline-offset-8">Authenticated Artifacts</h3>
                                {isEditing && (
                                    <button
                                        onClick={() => {
                                            const input = document.createElement('input');
                                            input.type = 'file';
                                            input.accept = 'application/pdf';
                                            input.onchange = (e) => {
                                                if (e.target.files.length > 0) {
                                                    alert(`Diagnostic Artifact "${e.target.files[0].name}" synchronized successfully! It will be transmitted to the registry upon save.`);
                                                }
                                            };
                                            input.click();
                                        }}
                                        className="px-4 py-2 bg-gray-50 border border-gray-200 text-[#155e75] text-[10px] font-black uppercase tracking-widest shadow-sm hover:bg-gray-100 transition-all"
                                    >
                                        + Upload Document
                                    </button>
                                )}
                            </div>
                            {device.documents && device.documents.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {device.documents.map(doc => (
                                        <DocCard key={doc.id} title={doc.title} date={doc.date} />
                                    ))}
                                </div>
                            ) : (
                                <div className="p-12 text-center border-2 border-dashed border-gray-200">
                                    <DocumentTextIcon className="w-10 h-10 text-gray-300 mx-auto mb-4" />
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No Documents Uploaded</p>
                                    <p className="text-[10px] text-gray-400 mt-2">Compliance artifacts will appear here once verified.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {isEditing && (
                    <div className="p-8 bg-[#155e75] flex justify-end gap-6 shadow-[0_-10px_20px_rgba(0,0,0,0.1)] sticky bottom-0">
                        <button onClick={() => setIsEditing(false)} className="px-8 py-3 text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all rounded-sm border border-white/30">Discard Manifest</button>
                        <button onClick={handleSave} className="flex items-center gap-3 px-12 py-3 bg-white text-[#155e75] text-[10px] font-black uppercase tracking-widest hover:bg-gray-100 transition-all rounded-sm shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 transition-all">
                            <CloudArrowUpIcon className="w-5 h-5" /> Commit Audit Updates
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

const Section = ({ header, children }) => (
    <div className="space-y-10">
        <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest border-l-4 border-[#155e75] pl-6 py-1">{header}</h3>
        <div className="grid grid-cols-1 gap-8 ml-6">
            {children}
        </div>
    </div>
);

const DataField = ({ label, value, isEditing, onChange, options }) => (
    <div className="group">
        <label className="block text-[10px] text-gray-400 font-bold uppercase mb-2 tracking-widest group-hover:text-[#155e75] transition-colors">{label}</label>
        {isEditing && onChange ? (
            options ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 p-3 text-xs font-bold text-[#155e75] outline-none focus:border-[#155e75] focus:bg-white rounded-none shadow-inner transition-all appearance-none select-arrow"
                >
                    {options.map(opt => <option key={opt.code} value={opt.code}>{opt.name}</option>)}
                </select>
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 p-3 text-xs font-bold text-[#155e75] outline-none focus:border-[#155e75] focus:bg-white rounded-none shadow-inner transition-all"
                />
            )
        ) : (
            <p className="text-sm text-gray-800 font-bold tracking-tight border-b border-gray-100 pb-2">{value || 'UNSET'}</p>
        )}
    </div>
);

const DocCard = ({ title, date }) => (
    <div className="p-6 border border-gray-100 bg-gray-50 hover:border-[#155e75] hover:bg-white transition-all cursor-pointer group">
        <DocumentTextIcon className="w-8 h-8 text-gray-300 group-hover:text-[#155e75] mb-4" />
        <p className="text-[10px] font-black uppercase text-gray-800 tracking-widest mb-1">{title}</p>
        <p className="text-[9px] font-bold text-gray-400">UPLOADED: {date}</p>
    </div>
);
